/**
 * SeoCanonical
 * Manages <head> SEO tags (canonical + meta description + optional title)
 * for each route in the SPA.
 *
 * - Canonical: self-referencing https://app.jyotishpuraan.com{pathname},
 *   query strings stripped so duplicate-content variants (e.g. ?intent=...)
 *   collapse to a single canonical URL.
 * - Description: <meta name="description"> kept in sync with route purpose.
 * - Title: <title> kept in sync (when provided).
 *
 * Pure side-effect on <head> — renders nothing.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const CANONICAL_ORIGIN = 'https://app.jyotishpuraan.com';

function setCanonical(url) {
  if (typeof document === 'undefined') return;
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  if (link.getAttribute('href') !== url) {
    link.setAttribute('href', url);
  }
}

function setMetaDescription(content) {
  if (typeof document === 'undefined' || !content) return;
  let meta = document.querySelector('meta[name="description"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'description');
    document.head.appendChild(meta);
  }
  if (meta.getAttribute('content') !== content) {
    meta.setAttribute('content', content);
  }
}

function setTitle(title) {
  if (typeof document === 'undefined' || !title) return;
  if (document.title !== title) document.title = title;
}

const SeoCanonical = ({ description, title }) => {
  const { pathname } = useLocation();

  useEffect(() => {
    const clean = pathname.length > 1 && pathname.endsWith('/')
      ? pathname.slice(0, -1)
      : pathname;
    setCanonical(`${CANONICAL_ORIGIN}${clean}`);
    if (description) setMetaDescription(description);
    if (title) setTitle(title);
  }, [pathname, description, title]);

  return null;
};

export default SeoCanonical;
